import { textLgSemibold, textMd, textMdSemibold } from '@insify/design-system/typography';
import { Hint, ReceiptContainer, ReceiptItem, useAB } from '@insify/funnel-components';
import { useDebouncedCallback } from '@insify/react-hooks';
import { capitalize } from '@insify/ui-utils';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getRealEstateAgentHintIfApplicable } from '../../../../../components/realEstateAgentHint';
import { useAddOns } from '../../../../../hooks/useAddOns';
import { useCoverageContent } from '../../../../../hooks/useCoverageContent';
import { useCoverageValues } from '../../../../../hooks/useCoverageValues';
import { usePriceBreakdownTranslations } from '../../../../../hooks/useFeeTranslations';
import { useCheckoutQuery, useConditionsQuery } from '../../../../../hooks/useQueries';
import { EmailSendNew } from '../EmailSendNew';

import styles from './Receipt.module.css';

import type { CoverageKey } from '../../../../../models/coverage';
import type { FeeType } from '@insify/funnel-components';
import type { CountryCode } from '@insify/global-countries';
import type { MoneyField } from '@insify/global-money';
import type { Namespace } from 'i18next';
import type { FC } from 'react';

type PriceBreakdown = Array<{
  type: Exclude<CoverageKey, 'legalProtection' | 'carInsurance'> | FeeType;
  amount?: MoneyField;
}>;

const ReceiptBreakdown: FC<{
  country: CountryCode;
  totalAmount: MoneyField | undefined;
  paymentBreakdown: PriceBreakdown;
}> = ({ country, paymentBreakdown, totalAmount }) => {
  const { t } = useTranslation<Namespace<'funnel'>>(['funnel']);
  const priceBreakdownTranslations = usePriceBreakdownTranslations();

  return (
    <>
      {paymentBreakdown
        .map(({ type, amount }) => ({ ...priceBreakdownTranslations[type], type, amount }))
        .map(({ type, amount, title, description }) => (
          <>
            <ReceiptItem
              key={type}
              country={country}
              title={title}
              titleClassName={clsx(textMd)}
              amount={amount}
              amountClassName={clsx(textMdSemibold)}
              description={description}
              data-testid='secondMonthPaymentValue'
            />
          </>
        ))}
      <hr />
      <ReceiptItem
        country={country}
        title={t('checkout_receipt_single_payment')}
        titleClassName={clsx(textMdSemibold)}
        amount={totalAmount}
        amountClassName={clsx(textLgSemibold, styles.priceHighlight)}
        after={t('including_tax')}
        data-testid='firstMonthTotal'
      />
    </>
  );
};

export const Receipt: FC<{
  className?: string;
  country: CountryCode;
}> = ({ className, country }) => {
  const { t } = useTranslation<Namespace<'funnel'>>(['funnel']);
  const { translations } = useCoverageContent();
  const { isLoading: isCoverageValuesLoading, premiums } = useCoverageValues();
  const addOns = useAddOns();
  const { data: conditionsData } = useConditionsQuery();
  const { data: checkoutData, isStale: isCheckoutStale, isLoading: isCheckoutLoading } = useCheckoutQuery();
  const ab = useAB();

  const [paymentBreakdown, setPaymentBreakdown] = useState<PriceBreakdown>([]);

  const showMonthsMessage = conditionsData?.riskCarriers?.length === 1 && conditionsData.riskCarriers[0] === 'wakam';

  useEffect(() => {
    const breakdown: PriceBreakdown = [];
    setBreakdownDebounced([...breakdown, ...(checkoutData?.checkoutFees ?? [])]);
  }, [checkoutData?.checkoutFees.length, premiums.coverages?.length, isCoverageValuesLoading, isCheckoutLoading]);

  // To prevent "jumping" price breakdown section while calculation requests.
  const setBreakdownDebounced = useDebouncedCallback((breakdown) => {
    if (isCoverageValuesLoading || isCheckoutLoading) return;
    setPaymentBreakdown(breakdown);
  }, 1000);

  const receiptBanners =
    conditionsData?.professionalLiability?.dailyCancellable ?? true
      ? [<Trans i18nKey='funnel:checkout_receipt_usp_no_commitment' key='usp1' />]
      : undefined;

  const hint = getRealEstateAgentHintIfApplicable();

  return (
    <ReceiptContainer className={className} banners={receiptBanners}>
      {hint && <Hint className={styles.hint}>{hint}</Hint>}
      {premiums.coverages?.map(({ type, amount }) => (
        <ReceiptItem
          key={type}
          country={country}
          title={translations[`receipt${capitalize(type)}` as keyof typeof translations] as string}
          titleClassName={clsx(textMd)}
          amount={amount}
          amountClassName={clsx(textMdSemibold)}
          description={showMonthsMessage ? t('checkout_receipt_first_month') : undefined}
          isLoading={isCoverageValuesLoading}
          data-testid='firstMonthPaymentValue'
        />
      ))}
      {addOns
        ?.filter((addOn) => addOn.name !== 'publicLiability') // Handled with the main coverage
        .map((addOn, index) => (
          <ReceiptItem
            key={addOn.name}
            country={'FR'}
            titleClassName={styles.listItem}
            title={<li>{addOn.title}</li>}
            amountOrPlaceholder={addOn.included && addOn.priceDisplay}
            amount={addOn.priceAmount}
            amountClassName={clsx(textMdSemibold)}
            data-testid={`addon-price-${index}`}
          />
        ))}
      {ab.experiments.exampleContractDownloadShown === 'receipt' && <EmailSendNew />}
      {isCheckoutStale === false && paymentBreakdown.length > 0 && (
        <ReceiptBreakdown
          paymentBreakdown={paymentBreakdown}
          country={country}
          totalAmount={checkoutData?.totalPayableCheckout}
        />
      )}
      <hr />
      <ReceiptItem
        country={country}
        title={t('coverage_receipt_total')}
        titleClassName={clsx(textMdSemibold)}
        amount={checkoutData?.monthlyPayable}
        amountClassName={clsx(textLgSemibold, styles.priceHighlight)}
        after={t('coverage_per_month')}
        isLoading={isCheckoutLoading}
        data-testid='checkoutTotal'
      />
      {showMonthsMessage && <i className={styles.wakamInfo}>{t('checkout_receipt_from_the_3rd_month')}</i>}
    </ReceiptContainer>
  );
};
